import { useLayoutEffect, useRef, useState } from 'react'
import { Patient } from '@medplum/fhirtypes'
import { Group } from '@mantine/core'

import { usePatientRisksGaps } from '@/hooks/usePatientRisksGaps'
import { RisksGapsReportPanel, RisksGapsStatus } from './RisksGapsReport'

type Props = {
  patient: Patient
}

// Risks & Gaps page (PD-1120, docs/cpg/RISKS_GAPS.md): the AI-generated Clinical Risk
// Analysis — chart risks + guideline-recommended care gaps. Same page anatomy as
// Clinical Summary minus the Details tab: one header row — title (left), the report's
// status controls (right) — above the scrolling report panel. The hook lives here so the
// header controls and the panel share one fetch/poll cycle; visiting the tab verifies
// staleness (chart hash + guideline corpus/citations) and auto-kicks a rebuild when
// stale. The page is sized to the viewport space below the chart chrome and the panel
// scrolls internally, so the header row never scrolls away.
export const RisksGaps = ({ patient }: Props) => {
  const report = usePatientRisksGaps(patient.id)
  const rootRef = useRef<HTMLDivElement | null>(null)
  const [height, setHeight] = useState<number>()

  // Measure the viewport space below the chart chrome (navbar + action bar). The chrome
  // height isn't constant — the action bar wraps on narrow widths and its sync badge
  // pops in async — so re-measure on window resizes and on any document reflow.
  useLayoutEffect(() => {
    const measure = () => {
      if (!rootRef.current) return
      // Window scroll carried over from another chart section would skew rect.top; with
      // the panel scrolling internally the window has nothing to scroll anyway.
      window.scrollTo(0, 0)
      setHeight(window.innerHeight - rootRef.current.getBoundingClientRect().top)
    }
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(document.body)
    window.addEventListener('resize', measure)
    return () => {
      observer.disconnect()
      window.removeEventListener('resize', measure)
    }
  }, [])

  return (
    <div ref={rootRef} className="flex flex-col p-4" style={{ height }}>
      <Group align="center" justify="space-between" wrap="nowrap" gap="md" mb="md">
        <h1 className="text-2xl font-bold whitespace-nowrap">Risks & Gaps</h1>
        <div className="min-w-0">
          <RisksGapsStatus report={report} />
        </div>
      </Group>
      <div className="min-h-0 flex-1 overflow-y-auto">
        <RisksGapsReportPanel report={report} />
      </div>
    </div>
  )
}
